import {ArrowBack, Visibility} from '@mui/icons-material';
import {Box, Button, Stack, Typography} from '@mui/material';
import moment from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IndividualPlan} from '../../../../../../../backend/interfaces/individual-plan';
import {ActivityType} from '../../../../../../../backend/interfaces/activity-type';
import {IndividualPlanHistoryEntry} from '../../../../../../../backend/interfaces/individual-plan-history';
import {BaseLogAction} from '../../../../../../../components/history-logs/common/base-log-action';
import {LogsEmpty} from '../../../../../../../components/history-logs/common/logs-empty';
import {LogsLoadMoreBtn} from '../../../../../../../components/history-logs/common/logs-load-more-btn';
import {LogsLoading} from '../../../../../../../components/history-logs/common/logs-loading';
import '../../../../../../../components/history-logs/history-logs.scss';
import {IndividualPlanAiGeneratedDiagnostic} from '../../../../../../../backend/responses/individual-plan-ai-generated';
import {Modal} from '../../../../../../../components/modal/modal';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {useUsersInfoCache} from '../../../../../../../hooks/use-users-info-cache';
import {translations} from '../../../../../../../i18n/translations';
import {IndividualPlansRepository} from '../../../../../../../repositories/individual-plans';
import {IndividualPlanDiagnosticsView} from './individual-plan-diagnostics-view';
import {
  IndividualPlanHistorySummaryLine,
  summarizeIndividualPlanChanges,
} from './individual-plan-history-summary';

const PAGE_SIZE = 20;
const MAX_SUMMARY_LINES = 4;

/**
 * Modal with the history of an individual plan: who changed it, when and what kind of change it
 * was. Latest first. Read only.
 */
export const IndividualPlanHistoryPanel = ({
  individualPlan,
  activityTypes,
  canWrite,
  onRestored,
  onConflict,
  onClose,
}: {
  individualPlan: Pick<IndividualPlan, 'id' | 'entityId' | 'userId' | 'revision'>;
  // Catalog of activity types, to show the names of the activities of a previous version
  activityTypes?: Array<ActivityType>;
  // Only users who can write can restore a version
  canWrite: boolean;
  // The plan after a version was restored
  onRestored: (individualPlan: IndividualPlan) => void;
  // The plan was changed by someone else while the user was restoring a version
  onConflict: () => void;
  onClose: () => void;
}) => {
  const {t} = useTranslation();
  const labels = translations.seniors.edit.senior_dcm.individual_plan.history;
  const {id, entityId, userId} = individualPlan;
  const snackBarContext = useSnackBar();

  const [entries, setEntries] = useState<Array<IndividualPlanHistoryEntry>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // Version being viewed (null shows the list)
  const [selectedEntry, setSelectedEntry] = useState<IndividualPlanHistoryEntry | null>(null);
  const [detail, setDetail] = useState<IndividualPlanHistoryEntry | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [hasDetailError, setHasDetailError] = useState<boolean>(false);
  const [isRestorePromptOpen, setIsRestorePromptOpen] = useState<boolean>(false);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);

  // Names and pictures of the authors
  const authorsIds = useMemo(() => entries.map((entry) => entry.userAuthorId), [entries]);
  const {users} = useUsersInfoCache(authorsIds);

  const loadPage = useCallback(
    async (before?: number) => {
      const page = await IndividualPlansRepository.instance.getIndividualPlanHistory(
        {id, entityId, userId},
        {limit: PAGE_SIZE, before}
      );
      setEntries((previous) => (before === undefined ? page : [...previous, ...page]));
      setHasMore(page.length === PAGE_SIZE);
    },
    [id, entityId, userId]
  );

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    loadPage()
      .catch((err) => {
        console.error(`Error: ${err}`);
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [loadPage]);

  function loadMore() {
    if (isLoadingMore || entries.length === 0) {
      return;
    }
    setIsLoadingMore(true);
    setHasError(false);
    // Entries come latest first, so the last one has the lowest revision
    loadPage(entries[entries.length - 1].revision)
      .catch((err) => {
        console.error(`Error: ${err}`);
        setHasError(true);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }

  function getChangeTypeLabel(entry: IndividualPlanHistoryEntry): string {
    const key = labels.change_type[entry.changeType];
    // The backend may add new change types
    return key ? t(key) : t(labels.change_type_unknown);
  }

  // A version can be restored by users who can write, once its content is loaded
  const canRestore = canWrite && !!selectedEntry && !!detail?.snapshot;

  function reloadList() {
    setIsLoading(true);
    setHasError(false);
    loadPage()
      .catch((err) => {
        console.error(`Error: ${err}`);
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  async function restoreSelectedEntry() {
    if (!selectedEntry || isRestoring) {
      return;
    }
    const restoreLabels = labels.restore;
    setIsRestoring(true);
    try {
      const restoredPlan = await IndividualPlansRepository.instance.restoreIndividualPlanRevision(
        {id, entityId, userId},
        selectedEntry.revision,
        {expectedRevision: individualPlan.revision}
      );
      onRestored(restoredPlan);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(restoreLabels.success, {revision: selectedEntry.revision}),
      });
    } catch (err: any) {
      console.error(`Error: ${err}`);
      const isConflict = err?.response?.status === 409;
      if (isConflict) {
        onConflict();
      }
      snackBarContext.setOfficeToastNotification({
        severity: isConflict ? 'warning' : 'error',
        message: t(isConflict ? restoreLabels.conflict : restoreLabels.error),
      });
    } finally {
      setIsRestoring(false);
      setIsRestorePromptOpen(false);
      // Back to the list, which now has the restore (or the changes of the other user)
      setSelectedEntry(null);
      reloadList();
    }
  }

  function openEntry(entry: IndividualPlanHistoryEntry) {
    setSelectedEntry(entry);
    setDetail(null);
    setHasDetailError(false);
    setIsLoadingDetail(true);
    IndividualPlansRepository.instance
      .getIndividualPlanHistoryRevision({id, entityId, userId}, entry.revision)
      .then((data) => {
        setDetail(data);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
        setHasDetailError(true);
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  }

  // Diagnostics of the viewed version, with the names of the activities
  const detailDiagnostics: Array<IndividualPlanAiGeneratedDiagnostic> = useMemo(
    () =>
      (detail?.snapshot?.diagnostics ?? []).map((diagnostic) => ({
        ...diagnostic,
        goals: diagnostic.goals?.map((goal) => ({
          ...goal,
          objectives: goal.objectives?.map((objective) => ({
            ...objective,
            activityTypesNames: (objective.activityTypesIds ?? [])
              .map(
                (activityTypeId) => activityTypes?.find((type) => type.id === activityTypeId)?.name
              )
              .filter((name): name is string => !!name),
          })),
        })),
      })),
    [detail, activityTypes]
  );

  function describeLine(line: IndividualPlanHistorySummaryLine): string {
    const summary = labels.summary;
    const entity = t(summary.entity[line.entityType]);
    const entities = t(summary.entity_plural[line.entityType]);

    if (line.type === 'updated') {
      const fieldKey = summary.field[line.field as keyof typeof summary.field];
      const field = fieldKey ? t(fieldKey) : line.field;
      if (line.count > 1) {
        return t(summary.updated_many, {count: line.count, entities, field});
      }
      const text = t(summary.updated, {entity, field});
      return line.oldValue || line.newValue
        ? `${text} ("${line.oldValue ?? '—'}" → "${line.newValue ?? '—'}")`
        : text;
    }

    const isCreated = line.type === 'created';
    if (line.count > 1) {
      return t(isCreated ? summary.created_many : summary.deleted_many, {
        count: line.count,
        entities,
      });
    }
    if (line.name) {
      return t(isCreated ? summary.created : summary.deleted, {entity, name: line.name});
    }
    return t(isCreated ? summary.created_unnamed : summary.deleted_unnamed, {entity});
  }

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      preventCloseClick
      title={t(labels.title)}
      actionButton={{type: 'close'}}
      actions={
        // The footer button restores the version being viewed (the X on the corner closes the modal)
        canRestore
          ? [
              {
                label: t(labels.restore.button),
                type: 'nondestructive',
                onClick: () => {
                  setIsRestorePromptOpen(true);
                },
              },
            ]
          : [
              {
                label: t(labels.close),
                type: 'neutral',
                onClick: onClose,
              },
            ]
      }
    >
      <Box className="history-logs">
        {selectedEntry && (
          <Stack gap="16px">
            <Box>
              <Button
                size="small"
                startIcon={<ArrowBack />}
                onClick={() => {
                  setSelectedEntry(null);
                }}
              >
                {t(labels.back)}
              </Button>
            </Box>
            <Box>
              <Typography sx={{fontWeight: 600}}>
                {[
                  t(labels.version, {revision: selectedEntry.revision}),
                  getChangeTypeLabel(selectedEntry),
                  moment(selectedEntry.registeredAt).format('L LT'),
                ].join(' • ')}
              </Typography>
              <Typography sx={{fontSize: '14px', opacity: 0.7}}>{t(labels.detail_note)}</Typography>
            </Box>
            {isLoadingDetail && <LogsLoading />}
            {hasDetailError && <Typography>{t(labels.detail_load_error)}</Typography>}
            {detail?.snapshot && (
              <>
                {!!detail.snapshot.observations && (
                  <Box>
                    <Typography sx={{fontWeight: 600}}>
                      {t(translations.seniors.edit.senior_dcm.individual_plan.observations)}
                    </Typography>
                    <Typography sx={{whiteSpace: 'pre-wrap'}}>
                      {detail.snapshot.observations}
                    </Typography>
                  </Box>
                )}
                {detailDiagnostics.length > 0 ? (
                  <IndividualPlanDiagnosticsView diagnostics={detailDiagnostics} />
                ) : (
                  <Typography>{t(labels.no_diagnostics)}</Typography>
                )}
              </>
            )}
          </Stack>
        )}
        {!selectedEntry && (
          <>
            {isLoading && <LogsLoading />}
            {!isLoading && hasError && <Typography>{t(labels.load_error)}</Typography>}
            {!isLoading && !hasError && entries.length === 0 && <LogsEmpty />}
            {entries.map((entry, entryIndex) => {
              const author = entry.userAuthorId ? users[entry.userAuthorId] : undefined;
              const authorName = author?.name ?? t(labels.unknown_author);
              const details = [
                t(labels.version, {revision: entry.revision}),
                getChangeTypeLabel(entry),
                entry.source ? t(labels.source[entry.source]) : undefined,
              ].filter((detail) => !!detail);
              const summaryLines = summarizeIndividualPlanChanges(entry.changes);
              const visibleLines = summaryLines.slice(0, MAX_SUMMARY_LINES);
              const hiddenLines = summaryLines.length - visibleLines.length;
              return (
                <Box
                  key={`revision-${entry.revision}`}
                  // The "View version" button is the accessible way to open it; clicking anywhere works too
                  onClick={() => openEntry(entry)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '5px',
                    padding: '8px',
                    marginTop: entryIndex > 0 ? '6px' : 0,
                    ':hover': {backgroundColor: 'action.hover'},
                  }}
                >
                  <BaseLogAction
                    title={authorName}
                    name={authorName}
                    picture={author?.picture}
                    date={moment(entry.registeredAt).format('L LT')}
                  >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px', width: '100%'}}>
                      <Typography>{details.join(' • ')}</Typography>
                      {entry.restoredFromRevision !== undefined && (
                        <Typography>
                          {t(labels.summary.restored_from, {revision: entry.restoredFromRevision})}
                        </Typography>
                      )}
                      {visibleLines.map((line, lineIndex) => (
                        <Typography key={`line-${lineIndex}`} sx={{opacity: 0.8}}>
                          {describeLine(line)}
                        </Typography>
                      ))}
                      {hiddenLines > 0 && (
                        <Typography sx={{opacity: 0.8}}>
                          {t(labels.summary.more, {count: hiddenLines})}
                        </Typography>
                      )}
                      <Box sx={{marginTop: '4px', display: 'flex', justifyContent: 'flex-end'}}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<Visibility />}
                          onClick={(event) => {
                            // The whole entry is clickable too, avoid opening it twice
                            event.stopPropagation();
                            openEntry(entry);
                          }}
                        >
                          {t(labels.view_version)}
                        </Button>
                      </Box>
                    </Box>
                  </BaseLogAction>
                </Box>
              );
            })}
            {isLoadingMore && (
              <Box sx={{marginTop: '14px'}}>
                <LogsLoading />
              </Box>
            )}
            {!isLoading && !isLoadingMore && hasMore && (
              <Box sx={{marginTop: '14px'}}>
                <LogsLoadMoreBtn onClick={loadMore} />
              </Box>
            )}
          </>
        )}
      </Box>
      {isRestorePromptOpen && selectedEntry && (
        <Modal
          open
          onClose={() => {
            setIsRestorePromptOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          preventCloseClick
          title={t(labels.restore.prompt_title, {revision: selectedEntry.revision})}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsRestorePromptOpen(false);
              },
              disabled: isRestoring,
            },
            {
              label: t(labels.restore.confirm),
              type: 'nondestructive',
              onClick: restoreSelectedEntry,
              loading: isRestoring,
            },
          ]}
        >
          <Typography>{t(labels.restore.prompt_description)}</Typography>
        </Modal>
      )}
    </Modal>
  );
};
